import { FC } from "react";

import { Raster, RasterTypeEnum } from "@nens/threedi/openapi-client";

import { getSchematisationsApi } from "../../apiConfiguration";
import GenerateAndOpenLink from "../../components/GenerateAndOpenLink";
import { GridCard } from "../../components/GridCard";

interface RasterDownloadLinksProps {
  id: string;
  revisionId: string;
  schematisationRevisionRasters: Raster[];
  onlyInitial?: boolean;
}

const RasterDownloadLinks: FC<RasterDownloadLinksProps> = ({
  id,
  revisionId,
  schematisationRevisionRasters,
  onlyInitial,
}) => {
  const filterRastersByType = (rasters: Raster[], type: RasterTypeEnum) => {
    if (onlyInitial && !type.includes("initial_")) return null;
    return rasters.filter((raster) => raster.type === type);
  };

  const filesByType = [
    {
      typeDisplayValue: "Digital Elevation Model",
      rasters: filterRastersByType(schematisationRevisionRasters, RasterTypeEnum.DEM_FILE),
    },
    {
      typeDisplayValue: "Initial Concentration File",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.INITIAL_CONCENTRATION_FILE
      ),
    },
    {
      typeDisplayValue: "Equilibrium Infiltration Rate",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.EQUILIBRIUM_INFILTRATION_RATE_FILE
      ),
    },
    {
      typeDisplayValue: "Friction Coefficient",
      rasters: filterRastersByType(schematisationRevisionRasters, RasterTypeEnum.FRICT_COEF_FILE),
    },
    {
      typeDisplayValue: "Initial Water Level",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.INITIAL_WATERLEVEL_FILE
      ),
    },
    {
      typeDisplayValue: "Initial Ground Water Level",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.INITIAL_GROUNDWATER_LEVEL_FILE
      ),
    },
    {
      typeDisplayValue: "Groundwater Hydro Connectivity",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.GROUNDWATER_HYDRO_CONNECTIVITY_FILE
      ),
    },
    {
      typeDisplayValue: "Groundwater Impervious Layer Level",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.GROUNDWATER_IMPERVIOUS_LAYER_LEVEL_FILE
      ),
    },
    {
      typeDisplayValue: "Initial Infiltration Rate",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.INITIAL_INFILTRATION_RATE_FILE
      ),
    },
    {
      typeDisplayValue: "Leakage",
      rasters: filterRastersByType(schematisationRevisionRasters, RasterTypeEnum.LEAKAGE_FILE),
    },
    {
      typeDisplayValue: "Phreatic Storage Capacity",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.PHREATIC_STORAGE_CAPACITY_FILE
      ),
    },
    {
      typeDisplayValue: "Hydraulic conductivity",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.HYDRAULIC_CONDUCTIVITY_FILE
      ),
    },
    {
      typeDisplayValue: "Porosity",
      rasters: filterRastersByType(schematisationRevisionRasters, RasterTypeEnum.POROSITY_FILE),
    },
    {
      typeDisplayValue: "Infiltration Rate",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.INFILTRATION_RATE_FILE
      ),
    },
    {
      typeDisplayValue: "Max infiltration capacity",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.MAX_INFILTRATION_CAPACITY_FILE
      ),
    },
    {
      typeDisplayValue: "Interception",
      rasters: filterRastersByType(schematisationRevisionRasters, RasterTypeEnum.INTERCEPTION_FILE),
    },
    {
      typeDisplayValue: "Vegetation Height",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.VEGETATION_HEIGHT_FILE
      ),
    },
    {
      typeDisplayValue: "Vegetation Drag Coefficient",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.VEGETATION_DRAG_COEFFICIENT_FILE
      ),
    },
    {
      typeDisplayValue: "Vegetation Stem Count",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.VEGETATION_STEM_COUNT_FILE
      ),
    },
    {
      typeDisplayValue: "Vegetation Diameter",
      rasters: filterRastersByType(
        schematisationRevisionRasters,
        RasterTypeEnum.VEGETATION_STEM_DIAMETER_FILE
      ),
    },
  ];

  const schematisationsApi = getSchematisationsApi();

  return (
    <>
      {filesByType.map(({ typeDisplayValue, rasters }) => {
        if (!rasters || rasters.length === 0) {
          return null;
        }
        return (
          <GridCard key={typeDisplayValue} colSpan={2} title={typeDisplayValue}>
            {(rasters || []).map((raster, indx) => {
              return (
                <GenerateAndOpenLink
                  key={raster.id + raster.name}
                  filename={raster.file?.filename || `rastern${indx}`}
                  size={raster.file?.size || 0}
                  fetchUrl={async () => {
                    if (!raster.id) return;
                    return (
                      await schematisationsApi.schematisationsRevisionsRastersDownload({
                        id: raster.id,
                        revisionPk: Number(revisionId),
                        schematisationPk: Number(id),
                      })
                    ).getUrl;
                  }}
                />
              );
            })}
          </GridCard>
        );
      })}
    </>
  );
};

export default RasterDownloadLinks;
